import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import {
  Search,
  Filter,
  Plus,
  DollarSign,
  CreditCard,
  Calendar,
  Users,
  TrendingUp,
  TrendingDown,
  AlertCircle,
  CheckCircle,
  Clock,
  X
} from 'lucide-react';
import paymentsService from '../../services/paymentsService.ts';
import PaymentCard from './PaymentCard';
import PaymentForm from './PaymentForm';
import PaymentDetails from './PaymentDetails';

const PaymentManagement = () => {
  const { t } = useTranslation();
  const creationDisabled = true;
  const [travelers, setTravelers] = useState([]);
  const [campaigns, setCampaigns] = useState([]);
  const [filteredTravelers, setFilteredTravelers] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCampaign, setSelectedCampaign] = useState('');
  const [selectedPaymentType, setSelectedPaymentType] = useState('');
  const [selectedPaymentStatus, setSelectedPaymentStatus] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const [selectedTraveler, setSelectedTraveler] = useState(null);
  const [isEditing, setIsEditing] = useState(false);

  useEffect(() => {
    const pickArray = (obj) => (Array.isArray(obj) ? obj : (obj?.data || []));
    const loadData = async () => {
      try {
        const today = new Date().toISOString().slice(0, 10);
        const [scheduleRes, balancesRes, travelersRes, groupsRes] = await Promise.all([
          paymentsService.getInstallmentSchedule({ include_paid: true, as_of: today }),
          paymentsService.getTravelerBalances({}),
          paymentsService.getTravelers({ per_page: 500, sort: 'name', dir: 'asc' }),
          paymentsService.getGroups({ per_page: 200, sort: 'id', dir: 'asc' }),
        ]);

        const scheduleArr = pickArray(scheduleRes);
        const balancesArr = pickArray(balancesRes);
        const travelersArr = pickArray(travelersRes);
        const groupsArr = pickArray(groupsRes);

        const travelerInfoMap = {};
        travelersArr.forEach((t) => {
          const id = t.id ?? t.traveler_id ?? t.tr_id;
          travelerInfoMap[id] = {
            id,
            fullName: t.name ?? t.traveler_name ?? 'Unknown',
            primaryPhone: t.phone1 ?? t.phone ?? t.mobile ?? '',
            passportNumber: t.passport ?? '',
          };
        });

        const balancesMap = {};
        balancesArr.forEach((b) => {
          const id = b.traveler_id ?? b.tr_id ?? b.id;
          balancesMap[id] = b;
        });

        const scheduleMap = {};
        scheduleArr.forEach((s) => {
          const id = s.traveler_id ?? s.tr_id ?? s.id;
          scheduleMap[id] = s;
        });

        const toCard = (s) => {
          const id = s?.traveler_id ?? s?.tr_id ?? s?.id;
          const info = travelerInfoMap[id] || {};
          const bal = balancesMap[id] || {};
          const total = Number(s?.total_price ?? bal?.total_price ?? 0);
          const remainingCalc = s?.remaining ?? bal?.balance ?? (total - (((bal?.cash_received ?? 0)) + (s?.paid_amount ?? 0)));
          const paymentType = s?.paytype || ((s?.interval_count ?? 0) > 1 ? 'installments' : 'cash');
          const status = remainingCalc <= 0
            ? 'paid'
            : (s?.overdue_count > 0
              ? 'overdue'
              : (((s?.paid_amount ?? (total - remainingCalc)) > 0 || (bal?.cash_received ?? 0) > 0) ? 'partial' : 'pending'));
          return {
            id,
            fullName: s?.traveler_name ?? info.fullName ?? 'Unknown',
            primaryPhone: info.primaryPhone || '',
            passportNumber: info.passportNumber || '',
            agentName: info.agentName,
            agentCommission: undefined,
            paymentType,
            totalAmount: total,
            remainingBalance: remainingCalc > 0 ? remainingCalc : 0,
            downPayment: bal?.cash_received ?? 0,
            monthlyInstallment: s?.price_each ?? 0,
            paymentDuration: s?.interval_count ?? 0,
            nextPaymentDate: s?.next_due_date,
            paymentStatus: status,
            campaignId: (s?.group_id ?? s?.gr_id ?? info.campaignId) ?? '',
          };
        };

        const ids = new Set([
          ...scheduleArr.map((s) => s.traveler_id ?? s.tr_id ?? s.id),
          ...balancesArr.map((b) => b.traveler_id ?? b.tr_id ?? b.id),
        ]);

        const cards = Array.from(ids).map((id) => {
          const s = scheduleMap[id];
          if (s) return toCard(s);
          const b = balancesMap[id] || {};
          const info = travelerInfoMap[id] || {};
          const total = Number(b?.total_price ?? 0);
          const down = Number(b?.cash_received ?? 0);
          const instPaid = Number(b?.installments_paid ?? 0);
          const remaining = Number(b?.balance ?? (total - down - instPaid));
          const status = remaining <= 0 ? 'paid' : ((down + instPaid) > 0 ? 'partial' : 'pending');
          return {
            id,
            fullName: info.fullName ?? 'Unknown',
            primaryPhone: info.primaryPhone || '',
            passportNumber: info.passportNumber || '',
            paymentType: instPaid > 0 ? 'installments' : 'cash',
            totalAmount: total,
            remainingBalance: remaining > 0 ? remaining : 0,
            downPayment: down,
            monthlyInstallment: 0,
            paymentDuration: 0,
            nextPaymentDate: undefined,
            paymentStatus: status,
            campaignId: info.campaignId || '',
          };
        });

        const normalizedCampaigns = groupsArr.map((g) => ({
          id: g.id ?? g.gr_id ?? g.group_id,
          campaignName: g.gr_notes || g.name || `Group ${g.id ?? g.gr_id ?? ''}`,
        }));

        setTravelers(cards);
        setFilteredTravelers(cards);
        setCampaigns(normalizedCampaigns);
      } catch (err) {
        console.error('Failed to load payment data', err);
        toast.error(t('common.somethingWentWrong'));
      }
    };
    loadData();
  }, []);

  useEffect(() => {
    // Filter travelers based on search and filters
    let filtered = travelers.filter(traveler => {
      const matchesSearch = searchTerm === '' || 
        traveler.fullName.toLowerCase().includes(searchTerm.toLowerCase()) ||
        traveler.primaryPhone.includes(searchTerm) ||
        traveler.passportNumber.toLowerCase().includes(searchTerm.toLowerCase()) ||
        traveler.agentName.toLowerCase().includes(searchTerm.toLowerCase());
      
      const matchesCampaign = selectedCampaign === '' || String(traveler.campaignId) === String(selectedCampaign);
      const matchesPaymentType = selectedPaymentType === '' || traveler.paymentType === selectedPaymentType;
      const matchesPaymentStatus = selectedPaymentStatus === '' || traveler.paymentStatus === selectedPaymentStatus;
      
      return matchesSearch && matchesCampaign && matchesPaymentType && matchesPaymentStatus;
    });
    
    setFilteredTravelers(filtered);
  }, [travelers, searchTerm, selectedCampaign, selectedPaymentType, selectedPaymentStatus]);

  const handleAddPayment = () => {
    if (creationDisabled) {
      toast.warn(t('payments.creationDisabled'));
      return;
    }
    setSelectedTraveler(null);
    setIsEditing(false);
    setShowForm(true);
  };

  const handleEditPayment = (traveler) => {
    setSelectedTraveler(traveler);
    setIsEditing(true);
    setShowForm(true);
  };

  const handleViewDetails = (traveler) => {
    setSelectedTraveler(traveler);
    setShowDetails(true);
  };

  const handleSavePayment = (paymentData) => {
    if (isEditing && selectedTraveler) {
      // Update existing payment
      const updatedTravelers = travelers.map(traveler => 
        traveler.id === selectedTraveler.id 
          ? { ...traveler, ...paymentData }
          : traveler
      );
      setTravelers(updatedTravelers);
      toast.success(t('payments.paymentUpdated'));
    } else {
      // Add new payment (this would typically create a new traveler or update existing one)
      toast.success(t('payments.paymentAdded'));
    }
    setShowForm(false);
    setSelectedTraveler(null);
    setIsEditing(false);
  };

  const handleDeletePayment = (travelerId) => {
    const updatedTravelers = travelers.filter(traveler => traveler.id !== travelerId);
    setTravelers(updatedTravelers);
    toast.success(t('payments.paymentDeleted'));
  };

  const clearFilters = () => {
    setSearchTerm('');
    setSelectedCampaign('');
    setSelectedPaymentType('');
    setSelectedPaymentStatus('');
  };

  // Calculate statistics
  const totalPayments = filteredTravelers.length;
  const totalRevenue = filteredTravelers.reduce((sum, traveler) => sum + traveler.totalAmount, 0);
  const paidTravelers = filteredTravelers.filter(t => t.paymentStatus === 'paid').length;
  const partialPayments = filteredTravelers.filter(t => t.paymentStatus === 'partial').length;
  const pendingPayments = filteredTravelers.filter(t => t.paymentStatus === 'pending').length;
  const overduePayments = filteredTravelers.filter(t => t.paymentStatus === 'overdue').length;
  const averageAmount = totalPayments > 0 ? totalRevenue / totalPayments : 0;

  const getCampaignName = (campaignId) => {
    const campaign = campaigns.find(c => c.id === campaignId);
    return campaign ? campaign.campaignName : 'Unknown Campaign';
  };

  return (
    <div className="p-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">
          {t('payments.title')}
        </h1>
        <p className="text-gray-600 dark:text-gray-400">
          {t('payments.subtitle')}
        </p>
      </div>

      {/* Statistics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600 dark:text-gray-400">
                {t('payments.totalPayments')}
              </p>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">
                {totalPayments}
              </p>
            </div>
            <DollarSign className="h-8 w-8 text-blue-600" />
          </div>
        </div>
        
        <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600 dark:text-gray-400">
                {t('payments.totalRevenue')}
              </p>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">
                ${totalRevenue.toLocaleString()}
              </p>
            </div>
            <TrendingUp className="h-8 w-8 text-green-600" />
          </div>
        </div>
        
        <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600 dark:text-gray-400">
                {t('payments.averageAmount')}
              </p>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">
                ${averageAmount.toLocaleString()}
              </p>
            </div>
            <TrendingDown className="h-8 w-8 text-purple-600" />
          </div>
        </div>
        
        <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600 dark:text-gray-400">
                {t('travelers.paidTravelers')}
              </p>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">
                {paidTravelers}
              </p>
            </div>
            <CheckCircle className="h-8 w-8 text-green-600" />
          </div>
        </div>
      </div>

      {/* Payment Status Overview */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
        <div className="bg-green-50 dark:bg-green-900/20 p-4 rounded-lg border border-green-200 dark:border-green-800">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-green-800 dark:text-green-200">
                {t('payments.paidPayments')}
              </p>
              <p className="text-xl font-bold text-green-900 dark:text-green-100">
                {paidTravelers}
              </p>
            </div>
            <CheckCircle className="h-6 w-6 text-green-600" />
          </div>
        </div>
        
        <div className="bg-yellow-50 dark:bg-yellow-900/20 p-4 rounded-lg border border-yellow-200 dark:border-yellow-800">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-yellow-800 dark:text-yellow-200">
                {t('payments.partialPayments')}
              </p>
              <p className="text-xl font-bold text-yellow-900 dark:text-yellow-100">
                {partialPayments}
              </p>
            </div>
            <Clock className="h-6 w-6 text-yellow-600" />
          </div>
        </div>
        
        <div className="bg-blue-50 dark:bg-blue-900/20 p-4 rounded-lg border border-blue-200 dark:border-blue-800">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-blue-800 dark:text-blue-200">
                {t('payments.pendingPayments')}
              </p>
              <p className="text-xl font-bold text-blue-900 dark:text-blue-100">
                {pendingPayments}
              </p>
            </div>
            <Calendar className="h-6 w-6 text-blue-600" />
          </div>
        </div>
        
        <div className="bg-red-50 dark:bg-red-900/20 p-4 rounded-lg border border-red-200 dark:border-red-800">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-red-800 dark:text-red-200">
                {t('payments.overduePayments')}
              </p>
              <p className="text-xl font-bold text-red-900 dark:text-red-100">
                {overduePayments}
              </p>
            </div>
            <AlertCircle className="h-6 w-6 text-red-600" />
          </div>
        </div>
      </div>

      {/* Search and Filters */}
      <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 mb-6">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
          {/* Search */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
            <input
              type="text"
              placeholder={t('searchPlaceholder')}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
            />
          </div>

          {/* Campaign Filter */}
          <select
            value={selectedCampaign}
            onChange={(e) => setSelectedCampaign(e.target.value)}
            className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
          >
            <option value="">{t('payments.allCampaigns')}</option>
            {campaigns.map(campaign => (
              <option key={campaign.id} value={campaign.id}>
                {campaign.campaignName}
              </option>
            ))}
          </select>

          {/* Payment Type Filter */}
          <select
            value={selectedPaymentType}
            onChange={(e) => setSelectedPaymentType(e.target.value)}
            className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
          >
            <option value="">{t('payments.allPaymentTypes')}</option>
            <option value="cash">{t('payments.cash')}</option>
            <option value="installments">{t('payments.installments')}</option>
          </select>

          {/* Payment Status Filter */}
          <select
            value={selectedPaymentStatus}
            onChange={(e) => setSelectedPaymentStatus(e.target.value)}
            className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
          >
            <option value="">{t('payments.allPaymentStatuses')}</option>
            <option value="paid">{t('payments.paid')}</option>
            <option value="partial">{t('payments.partial')}</option>
            <option value="pending">{t('payments.pending')}</option>
            <option value="overdue">{t('payments.overdue')}</option>
          </select>

          {/* Clear Filters */}
          <button
            onClick={clearFilters}
            className="flex items-center justify-center px-4 py-2 text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
          >
            <X className="h-4 w-4 mr-2" />
            {t('common.clear')}
          </button>
        </div>
      </div>

      {/* Action Bar */}
      <div className="flex justify-between items-center mb-6">
        <div className="flex items-center space-x-4">
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
            {t('payments.paymentsList')} ({filteredTravelers.length})
          </h2>
        </div>
        <button
          onClick={handleAddPayment}
          disabled={creationDisabled}
          title={creationDisabled ? t('common.warning') : t('payments.recordPayment')}
          className="flex items-center px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-lg transition-colors disabled:opacity-60"
        >
          <Plus className="h-4 w-4 mr-2" />
          {t('payments.recordPayment')}
        </button>
      </div>

      {/* Payments List */}
      {filteredTravelers.length === 0 ? (
        <div className="text-center py-12">
          <Users className="mx-auto h-12 w-12 text-gray-400 mb-4" />
          <p className="text-gray-500 dark:text-gray-400 mb-4">
            {searchTerm || selectedCampaign || selectedPaymentType || selectedPaymentStatus
              ? t('payments.noPaymentsFound')
              : t('payments.noPaymentsYet')
            }
          </p>
          {!searchTerm && !selectedCampaign && !selectedPaymentType && !selectedPaymentStatus && (
            <button
              onClick={handleAddPayment}
              disabled={creationDisabled}
              title={creationDisabled ? t('common.warning') : t('payments.recordPayment')}
              className="flex items-center mx-auto px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-lg transition-colors disabled:opacity-60"
            >
              <Plus className="h-4 w-4 mr-2" />
              {t('payments.recordPayment')}
            </button>
          )}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredTravelers.map(traveler => (
            <PaymentCard
              key={traveler.id}
              traveler={traveler}
              campaignName={getCampaignName(traveler.campaignId)}
              onEdit={() => handleEditPayment(traveler)}
              onDelete={() => handleDeletePayment(traveler.id)}
              onViewDetails={() => handleViewDetails(traveler)}
            />
          ))}
        </div>
      )}

      {/* Payment Form Modal */}
      {showForm && (
        <PaymentForm
          traveler={selectedTraveler}
          campaigns={campaigns}
          isEditing={isEditing}
          onSave={handleSavePayment}
          onCancel={() => {
            setShowForm(false);
            setSelectedTraveler(null);
            setIsEditing(false);
          }}
        />
      )}

      {/* Payment Details Modal */}
      {showDetails && selectedTraveler && (
        <PaymentDetails
          traveler={selectedTraveler}
          campaignName={getCampaignName(selectedTraveler.campaignId)}
          onClose={() => {
            setShowDetails(false);
            setSelectedTraveler(null);
          }}
          onEdit={() => {
            setShowDetails(false);
            handleEditPayment(selectedTraveler);
          }}
        />
      )}
    </div>
  );
};

export default PaymentManagement;